import { Component, DestroyRef, inject, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { ConversionListingService } from '../../../services/common/conversion-listing.service';
import { finalize, merge } from 'rxjs';
import {
  ConversionData,
  ConversionProgressPayload,
  MbrsStatusEnum,
} from '../../../../types/commons';
import { ConversionEventService } from '../../../services/common/conversion-event.service';
import { ConversionProgressService } from '../../../services/signalr/conversion-progress.service';
import { ConversionStatusResolverService } from '../../../services/mbrs/conversion-status-resolver.service';
import { isCompanyClickable } from '../../../utils/conversion-status.util';
import { filingEntryState } from '../../../utils/navigation-diagnostic-scope.util';
import { OverflowTooltipDirective } from '../../../directives/overflow-tooltip.directive';
import { buildPageLoadFailureNotice } from '../../../utils/page-load-failure.util';

@Component({
  selector: 'dashboard-recent-conversion',
  imports: [
    CardModule,
    TableModule,
    CommonModule,
    TooltipModule,
    SkeletonModule,
    RouterLink,
    OverflowTooltipDirective,
  ],
  templateUrl: './recent-conversion.component.html',
})
export class RecentConversionComponent implements OnInit {
  isLoading = true;
  isTableLoading = signal<boolean>(false);
  files = signal<ConversionData[]>([]);
  file = signal<ConversionData | null>(null);
  conversionId = signal<string>('');
  conversionListingService = inject(ConversionListingService);
  conversionEvent = inject(ConversionEventService);
  router = inject(Router);
  conversionProgressService = inject(ConversionProgressService);
  private statusResolver = inject(ConversionStatusResolverService);
  private destroyRef = inject(DestroyRef);
  MbrsStatusEnum = MbrsStatusEnum;
  // Shared row-interactivity rule (Success / SuccessWithWarnings / Review are
  // clickable; InProgress / PendingValidation / Failed / AwaitingFiles are not).
  // Reused from the util so this widget stays consistent with the xbrl-files
  // listing instead of hand-rolling a `== InProgress` check.
  protected readonly isCompanyClickable = isCompanyClickable;
  protected readonly filingEntryState = filingEntryState;

  fetchConversionListing(limit?: number) {
    this.isTableLoading.set(true);
    this.conversionListingService
      .fetchConversionListing({
        limit: limit ?? 8,
      })
      .pipe(finalize(() => this.isTableLoading.set(false)))
      .subscribe({
        next: (data) => {
          if (data.conversions.totalCount > 0) {
            this.files.set(data.conversions.items);
            // AS-842: any rows that arrived in PendingValidation kick off the
            // silent validation -> POST cycle now, same as the xbrl-files
            // listing. The chip flips when the resolver emits the terminal.
            data.conversions.items.forEach((row) =>
              this.maybeKickOffPendingValidation(row)
            );
          }
        },
        // Console-only, deliberately: this widget sits inside the dashboard
        // page, which already gets its own load-failure toast
        // (DashboardComponent.fetchConversionStatusCount). A second toast for
        // a sub-widget inside an already-surfaced page would be noise.
        error: (error: unknown) => {
          buildPageLoadFailureNotice('RECENT_CONVERSION', error);
        },
      });
  }

  /**
   * AS-842 — for any recent-conversion row in `PendingValidation`, run the
   * silent validation and swap the row's `status` in `files()` to the
   * derived terminal once it resolves. Mirrors
   * `xbrl-files.component.ts`'s `maybeKickOffPendingValidation`; the POST
   * write-back is handled inside the resolver, and the resolver's in-flight
   * map + the validator cache de-dupe a conversion already being resolved by
   * the xbrl-files listing.
   *
   * No toast is fired here — the dashboard widget only reflects the resolved
   * chip; terminal toast UX stays owned by the xbrl-files listing.
   */
  private maybeKickOffPendingValidation(row: ConversionData): void {
    if (row.status !== MbrsStatusEnum.PendingValidation) return;

    this.statusResolver
      .resolve(row)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((terminal) => {
        const updated = this.files().map((file) =>
          file.id === row.id ? { ...file, status: terminal } : file
        );
        this.files.set(updated);
      });
  }

  ngOnInit(): void {
    this.conversionEvent.refetch$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fetchConversionListing());
    this.fetchConversionListing();

    // Single handler for both Phase-1 and Phase-2 progress events — the row
    // update is identical. `phase` is threaded onto the row below for the
    // chip/interactivity gates; download stays Phase-2-only by nature (a
    // Phase 1 row never reaches a downloadable Success terminal — see the
    // isPhase1/isPhase2 note in `src/types/commons.ts`).
    merge(
      this.conversionProgressService.progress1st$,
      this.conversionProgressService.progress2nd$
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: ConversionProgressPayload) => {
        const updatedFiles = this.files().map((file) =>
          file.id === data.conversionId
            ? {
                ...file,
                status: data.status,
                phase: data.phase ?? file.phase,
                company: data.companyName,
              }
            : file
        );

        this.files.set(updatedFiles);

        // AS-842: a PendingValidation arrival via SignalR kicks off the
        // silent validation -> POST cycle so the dashboard chip resolves
        // without needing the xbrl-files listing to be opened.
        if (data.status === MbrsStatusEnum.PendingValidation) {
          const row = updatedFiles.find((file) => file.id === data.conversionId);
          if (row) this.maybeKickOffPendingValidation(row);
        }
      });
  }
}
